import { useRefresh } from "ra-core"
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { push } from "connected-react-router"
import { Box, Button, Card, CardContent, CardHeader, Checkbox, Divider, FormControl, FormControlLabel,
  FormGroup, FormHelperText, FormLabel, Grid, Stack, Typography, useMediaQuery, alpha,
  Drawer} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { InviteUserInput, RoleResponseDto } from "@/gen/api/user"
import retrieveAxiosErr from "@/utils/retrieveAxiosErr"
import OdForm from "@/components/formik/OdForm"
import TextField from "@/components/formik/TextField"
import Create from "@/controller/Create"
import WithSave from "@/controller/WithSave"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import { useUserManageAmbientDataContext } from "./UserManageAmbientDataContextProvider"
import { getUserProfile } from "@/reducers/userProfile"



interface States {
  open: boolean,
  emails: string[]|null,
  selectedRoles: {
    admin: Record<number, boolean>;
    custom: Record<number, boolean>;
  },
  submitting: boolean,
  errMsg: string|null,
}

const emailRule = Yup.string().max(50, 'Email must be at most 50 characters').email('Invalid email');

const validationSchema = 
  Yup.object().shape({
    email: Yup.string().required('Email Address is required')
  })

const initialValues = {
  email: '',
}
  
export const StartInvite: FC = () => {
  const [states, setStates] = useState<States>({
    open: false,
    emails: null,
    selectedRoles: { admin: {}, custom: {} },
    submitting: false,
    errMsg: null,
  })
  const userProfile = useSelector(getUserProfile)

  const {roles} = useUserManageAmbientDataContext()

  const changeRole = useCallback((role: RoleResponseDto) => (e: ChangeEvent<HTMLInputElement>) => {
    if (!role.roleId) { return; }

    setStates(v => {
      const selections =
        role.isDefault 
        ? { custom: v.selectedRoles.custom, admin: { ...v.selectedRoles.admin, [role.roleId!]: e.target.checked} }
        : { admin: v.selectedRoles.admin, custom: { ...v.selectedRoles.custom, [role.roleId!]: e.target.checked} }
      return {...v, selectedRoles: selections}
    })

  }, [setStates])

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const formikCtrl = useFormik({ initialValues, onSubmit: () => {}, validationSchema, enableReinitialize: true })
  const { values, errors, resetForm, setFieldValue, registerField } = formikCtrl

  useEffect(() => {
    registerField('email', {
      validate: (s:string) => {
        if (!s) { return 'Email Address is required' }
        const emails = s.split(';').map(x => x.trim().replaceAll(/[\r\n]/g, '')).filter(x => Boolean(x))
        if (emails.length === 0) { return 'Email Address is required' }
        let n = -1
        const distincted = new Set<string>()
        const error = emails.reduce((acc, email, i) => {
          if (acc) { return acc }
          if (distincted.has(email)) { return }
          n = i
          if (n >= 500) { 
            n = -1
            return { errors: ['Please input no more than 500 emails at one time'] }
          }
          try {
            emailRule.validateSync(email)
          } catch (error) {
            return error
          }
          distincted.add(email)
        }, null as any)
        if (error?.errors) { return error.errors[0] + (n > -1 ? ` (at position ${n + 1})` : '') }
        if (distincted.size > 500) { return 'Please input no more than 500 emails at one time' }
        setTimeout(() => setStates(v => ({...v, emails: [...distincted]})))
        return
      }
    })
  }, [registerField])
  
  const handlers = useDefaultSavedHandlers("Invitation sent")
  const refresh = useRefresh()
  const cbs = useMemo(() => ({
    onSuccess: () => {
      setStates(v => ({...v, open: false}))
      resetForm({
        status: { success: true },
        values: initialValues,
        isSubmitting: false,
      })
    },
    onFailure: (err:any) => {
      const errMsg = retrieveAxiosErr(err)
      setStates(v => ({...v, submitting: false, errMsg }))
    },
  }), [setStates, resetForm])
  handlers.onSuccess.merge(refresh as any)
  handlers.onSuccess.merge(cbs.onSuccess)
  handlers.onFailure.merge(cbs.onFailure)

  const handleSave = (save: (record: any) => void) => () => {
    if (errors.email) { return }

    const input: InviteUserInput = {
      emails: states.emails,
      roleIds: Object.entries(states.selectedRoles.custom).reduce<number[]>((acc, pair) => {
        if (pair[1]) { acc.push(+pair[0]) }
        return acc
      }, []),
      defaultRoleIds: Object.entries(states.selectedRoles.admin).reduce<number[]>((acc, pair) => {
        if (pair[1]) { acc.push(+pair[0]) }
        return acc
      }, []),
    }
    if (states.selectedRoles.admin[7]) { // 7: 'SecureFile Only' role
      if (input.roleIds!.length > 0 || input.defaultRoleIds!.length > 1) {
        setStates(v => ({...v, errMsg: '"SecureFile Only" role can not be assigned together with other roles'}))
        return
      }
    }

    setStates(v => ({...v, submitting: true, errMsg: null}))
    save(input)
  }

  useEffect(() => {
    if (!states.open) {
      setStates(v => ({...v, selectedRoles: { admin: {}, custom: {} }, submitting: false, errMsg: null, emails: null}))
      resetForm({
        values: initialValues,
      })
    }
  }, [states.open, resetForm, setStates])
  
  const location = useLocation()
  const dispatch = useDispatch()
  useEffect(() => {
    if (location.search) {
      const ps = new URLSearchParams(location.search)
      if (ps.get('invite-new-user')) {
        const prefill = ps.get('invite-new-user')
        setFieldValue('email', prefill)
        setStates(v => ({...v, open: true}))
        dispatch(push({...location, search:undefined}))
      }
    }
  }, [location, setFieldValue, setStates, dispatch])

  return (
    <>
      <Button
        color="primary"
        type="button"
        variant="contained"
        onClick={() => setStates(v => ({...v, open: true}))}
      >
        Invite New User
      </Button>
      <Drawer
        anchor="right"
        // onOpen={() => {}}
        onClose={() => setStates(v => ({...v, open: false}))}
        open={states.open}
        PaperProps={{ sx: { p: 2, width: width, } }} 
      >
        <Card>
          <CardHeader title="Invite New User" />
          <CardContent sx={{ height: 'calc(100vh - 130px)', overflowY: 'auto' }}>
            {userProfile?.isSsoEnabled &&
              <Box sx={{ px: 2, py: 1, mt: -1, mb: 2,
                borderRadius: 0.5,
                border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                bgcolor: theme => alpha(theme.palette.warning.light, 0.1),
              }}>
                <Typography variant="caption">
                  <b>Single Sign-On</b> (SSO) is configured with your organization’s Identity and Access Management (IAM) system. All users, user roles and data access roles are managed at the IAM system.
                </Typography>
              </Box>}
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <OdForm formikCtrl={formikCtrl}>
                    <TextField
                      disabled={states.submitting || userProfile?.isSsoEnabled}
                      multiline
                      maxRows={10}
                      label="Email Address*"
                      name="email"
                      />
                    <FormHelperText>Input multiple emails by inserting ";" as delimiter</FormHelperText>
                  </OdForm>
                </Grid>
                <Grid item xs={12}>
                  <FormControl sx={{ ml: 3, width: 'calc(100% - 18px)' }} component="fieldset" variant="standard">
                    <FormLabel component="legend">Assign roles</FormLabel>
                    <Box sx={{ overflowY: 'auto', height: 640 }}>
                      <FormGroup>
                        {roles.map(r =>
                          <FormControlLabel
                            control={
                              <Checkbox 
                                checked={states.selectedRoles[r.isDefault ? 'admin' : 'custom'][r.roleId || -1] || false} 
                                onChange={changeRole(r)} 
                                name={r.name || ''} 
                                disabled={userProfile?.isSsoEnabled}
                              />
                            }
                            key={`${r.organizationId}-${r.roleId }`}
                            label={r.name || ''}
                          />
                        )}
                      </FormGroup>
                    </Box>
                  </FormControl>
                </Grid>
              </Grid>
              {states.errMsg && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <FormHelperText error>
                    {states.errMsg}
                  </FormHelperText>
                </Box>
              )}
              <Divider sx={{ mx: -2, mb: 2 }} />
              <Stack direction="row" spacing={4} sx={{ justifyContent: 'space-between' }}>
                <Button
                  color="primary"
                  disabled={states.submitting}
                  variant="outlined"
                  onClick={() => setStates(v => ({...v, open: false}))}
                >
                  Cancel
                </Button>
                <Create resource="user-invitation" basePath="" {...handlers} >
                  <WithSave render={({save, saving}) => (
                    <Button
                      color="primary"
                      disabled={Boolean(errors.email || saving || userProfile?.isSsoEnabled)}
                      type="submit"
                      variant="contained"
                      onClick={handleSave(save)}
                    >
                      Send Invitation
                    </Button>
                  )} />
                </Create>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Drawer>
    </>
  )
}

export default StartInvite;


